import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { EQUIPMENT_CONDITIONS, EQUIPMENT_DURATIONS, EQUIPMENT_OPTIONS } from "../lib/constants";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Loader2, CheckCircle2, Accessibility } from "lucide-react";

const emptyForm = {
  name: "",
  nik: "",
  birth_place: "",
  birth_date: "",
  dukuh: "",
  desa: "",
  rt: "",
  rw: "",
  kecamatan: "",
  phone: "",
  equipment: "kursi_roda",
  user_condition: "sakit",
  duration: "1_bulan",
};

const calcAge = (birthDate) => {
  if (!birthDate) return "";
  const bd = new Date(`${birthDate}T00:00:00`);
  if (isNaN(bd.getTime())) return "";
  const now = new Date();
  let age = now.getFullYear() - bd.getFullYear();
  if (now.getMonth() < bd.getMonth() || (now.getMonth() === bd.getMonth() && now.getDate() < bd.getDate())) age -= 1;
  return age >= 0 ? age : "";
};

export default function PinjamAlat() {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const age = calcAge(form.birth_date);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Nama dan No. WhatsApp wajib diisi");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/public/equipment-loans", form);
      toast.success("Permohonan pinjam alat kesehatan berhasil dikirim");
      const sent = (data.wa_notifs || []).filter((w) => w.ok).length;
      if (sent > 0) toast.success(`Notifikasi WA terkirim ke ${sent} nomor`);
      setSuccess(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="pinjam-alat-page">
      <header className="border-b border-slate-200 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-3">
          <Link to="/"><img src="/logo-lazisnu.png" alt="Logo LAZISNU Dawe" className="h-10 w-10 rounded-lg bg-white object-contain p-0.5" /></Link>
          <div className="flex-1">
            <p className="font-heading font-bold text-slate-900 leading-tight">Pinjam Alat Kesehatan</p>
            <p className="text-xs text-slate-500">NU Care LAZISNU Dawe — Gratis</p>
          </div>
          <Link to="/">
            <Button variant="outline" size="sm" data-testid="pinjam-alat-back-button">
              <ArrowLeft className="mr-1.5 h-4 w-4" /> Kembali
            </Button>
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-8">
        {success ? (
          <Card data-testid="pinjam-alat-success">
            <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
              <CheckCircle2 className="h-14 w-14 text-emerald-700" />
              <p className="font-heading text-xl font-bold text-slate-800">Permohonan Terkirim</p>
              <p className="text-sm text-slate-500 max-w-md">
                Data peminjaman alat kesehatan Anda sudah masuk. Petugas kami akan menghubungi Anda untuk proses selanjutnya. Terima kasih.
              </p>
              <Link to="/">
                <Button className="mt-2 bg-emerald-800 hover:bg-emerald-700 text-white" data-testid="pinjam-alat-success-back">Kembali ke Beranda</Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <Card data-testid="pinjam-alat-form-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Accessibility className="h-5 w-5 text-emerald-800" />
                Form Pinjam Alat Kesehatan
              </CardTitle>
              <p className="text-xs text-slate-500">Layanan peminjaman alat bantu kesehatan gratis dari NU Care LAZISNU Dawe — isi data di bawah ini tanpa perlu login</p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="pinjam-alat-form">
                <div className="space-y-2">
                  <Label>Nama *</Label>
                  <Input data-testid="pinjam-alat-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
                </div>
                <div className="space-y-2">
                  <Label>NIK</Label>
                  <Input data-testid="pinjam-alat-nik-input" placeholder="Nomor Induk Kependudukan" value={form.nik} onChange={(e) => setForm({ ...form, nik: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>No. WhatsApp *</Label>
                  <Input data-testid="pinjam-alat-phone-input" placeholder="08xxxxxxxxxx" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required />
                </div>
                <div className="space-y-2">
                  <Label>Tempat Lahir</Label>
                  <Input data-testid="pinjam-alat-birth-place-input" value={form.birth_place} onChange={(e) => setForm({ ...form, birth_place: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Tanggal Lahir</Label>
                  <Input type="date" data-testid="pinjam-alat-birth-date-input" value={form.birth_date} onChange={(e) => setForm({ ...form, birth_date: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Usia (otomatis)</Label>
                  <Input data-testid="pinjam-alat-usia-input" value={age !== "" ? `${age} tahun` : ""} readOnly className="bg-slate-50" placeholder="Terisi otomatis" />
                </div>
                <div className="space-y-2">
                  <Label>Dukuh</Label>
                  <Input data-testid="pinjam-alat-dukuh-input" value={form.dukuh} onChange={(e) => setForm({ ...form, dukuh: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Desa</Label>
                  <Input data-testid="pinjam-alat-desa-input" value={form.desa} onChange={(e) => setForm({ ...form, desa: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>RT</Label>
                  <Input data-testid="pinjam-alat-rt-input" value={form.rt} onChange={(e) => setForm({ ...form, rt: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>RW</Label>
                  <Input data-testid="pinjam-alat-rw-input" value={form.rw} onChange={(e) => setForm({ ...form, rw: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Kecamatan</Label>
                  <Input data-testid="pinjam-alat-kecamatan-input" value={form.kecamatan} onChange={(e) => setForm({ ...form, kecamatan: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Alat Bantu Kesehatan</Label>
                  <Select value={form.equipment} onValueChange={(v) => setForm({ ...form, equipment: v })}>
                    <SelectTrigger data-testid="pinjam-alat-equipment-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {EQUIPMENT_OPTIONS.map((o) => (
                        <SelectItem key={o.key} value={o.key} data-testid={`pinjam-alat-equipment-${o.key}`}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Kondisi Pengguna</Label>
                  <Select value={form.user_condition} onValueChange={(v) => setForm({ ...form, user_condition: v })}>
                    <SelectTrigger data-testid="pinjam-alat-condition-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {EQUIPMENT_CONDITIONS.map((o) => (
                        <SelectItem key={o.key} value={o.key} data-testid={`pinjam-alat-condition-${o.key}`}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Jangka Waktu Pinjam</Label>
                  <Select value={form.duration} onValueChange={(v) => setForm({ ...form, duration: v })}>
                    <SelectTrigger data-testid="pinjam-alat-duration-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {EQUIPMENT_DURATIONS.map((o) => (
                        <SelectItem key={o.key} value={o.key} data-testid={`pinjam-alat-duration-${o.key}`}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button type="submit" data-testid="pinjam-alat-submit-button" disabled={saving} className="w-full bg-emerald-800 hover:bg-emerald-700 text-white">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Kirim Permohonan"}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
